import { useState, useRef, useEffect } from 'react';

// Voice chat hook — push-to-talk + hands-free conversation mode.
// Records mic audio (MediaRecorder), detects end-of-utterance via silence (Web Audio RMS),
// delegates the send to the panel (onSendAudio), and plays back the AI's spoken reply.
//
// PLAYBACK NOTE: replies are played through a Web Audio AudioContext that is created
// + resumed SYNCHRONOUSLY inside the user's tap (mic button / hands-free toggle).
// This "unlocks" audio on Safari/iOS/Chrome — a fresh `new Audio().play()` seconds
// after the gesture gets silently blocked by autoplay policies.

const SILENCE_MS = 1600;          // silence gap that ends an utterance (hands-free)
const MAX_UTTERANCE_MS = 30000;   // safety cap per utterance
const RMS_THRESHOLD = 0.022;      // voice activity threshold
const MIN_BLOB_BYTES = 3000;      // ignore tiny noise-only blobs

const pickMimeType = () => {
  if (typeof MediaRecorder === 'undefined') return '';
  if (MediaRecorder.isTypeSupported('audio/webm')) return 'audio/webm';
  if (MediaRecorder.isTypeSupported('audio/mp4')) return 'audio/mp4';
  return '';
};

export default function useVoiceChat({ onSendAudio, onError }) {
  // idle | listening (hands-free) | recording (push-to-talk) | processing | speaking
  const [voiceState, setVoiceState] = useState('idle');
  const [conversationMode, setConversationMode] = useState(false);

  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const recorderRef = useRef(null);
  const sourceRef = useRef(null);   // currently-playing AudioBufferSourceNode
  const vadTimerRef = useRef(null);
  const modeRef = useRef(false);
  const hadSpeechRef = useRef(false);
  const lastVoiceRef = useRef(0);
  const utterStartRef = useRef(0);
  const mimeRef = useRef(pickMimeType());

  // Keep latest callbacks (avoid stale closures across conversation turns)
  const onSendRef = useRef(onSendAudio);
  const onErrorRef = useRef(onError);
  useEffect(() => { onSendRef.current = onSendAudio; onErrorRef.current = onError; });

  // MUST be called synchronously inside a user gesture — unlocks audio playback.
  const ensureAudioCtx = () => {
    if (!audioCtxRef.current) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      audioCtxRef.current = new Ctx();
    }
    if (audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume().catch(() => {});
    }
    return audioCtxRef.current;
  };

  const cleanupVad = () => {
    if (vadTimerRef.current) { clearInterval(vadTimerRef.current); vadTimerRef.current = null; }
  };

  const stopPlayback = () => {
    if (sourceRef.current) {
      try { sourceRef.current.onended = null; sourceRef.current.stop(); } catch (e) {}
      sourceRef.current = null;
    }
  };

  const releaseStream = () => {
    cleanupVad();
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') {
      rec._discard = true;
      try { rec.stop(); } catch (e) {}
    }
    recorderRef.current = null;
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    analyserRef.current = null; // re-created with next stream (AudioContext stays alive/unlocked)
  };

  const ensureStream = async () => {
    if (streamRef.current && streamRef.current.active) return streamRef.current;
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
    });
    streamRef.current = stream;
    analyserRef.current = null; // stale analyser belongs to old stream
    return stream;
  };

  const ensureAnalyser = (stream) => {
    if (analyserRef.current) return analyserRef.current;
    const ctx = ensureAudioCtx();
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 2048;
    source.connect(analyser);
    analyserRef.current = analyser;
    return analyser;
  };

  const currentRms = () => {
    const analyser = analyserRef.current;
    if (!analyser) return 0;
    const data = new Uint8Array(analyser.fftSize);
    analyser.getByteTimeDomainData(data);
    let sum = 0;
    for (let i = 0; i < data.length; i++) {
      const v = (data[i] - 128) / 128;
      sum += v * v;
    }
    return Math.sqrt(sum / data.length);
  };

  const startRecorder = (stream) => {
    const chunks = []; // per-recorder closure — avoids races between restarts
    const rec = new MediaRecorder(stream, mimeRef.current ? { mimeType: mimeRef.current } : undefined);
    rec._discard = false;
    rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };
    rec.onstop = () => {
      if (rec._discard) return;
      handleUtterance(new Blob(chunks, { type: mimeRef.current || 'audio/webm' }));
    };
    rec.start();
    recorderRef.current = rec;
  };

  const handleUtterance = async (blob) => {
    if (blob.size < MIN_BLOB_BYTES) {
      // Too short — likely noise. Resume listening if hands-free.
      if (modeRef.current) startListening(); else setVoiceState('idle');
      return;
    }
    setVoiceState('processing');
    let result = null;
    try {
      result = await onSendRef.current(blob, mimeRef.current || 'audio/webm');
    } catch (err) {
      if (onErrorRef.current) onErrorRef.current('send');
      // Stop hands-free on failure to avoid error loops
      modeRef.current = false;
      setConversationMode(false);
      releaseStream();
      setVoiceState('idle');
      return;
    }
    if (result && result.audioBase64) {
      playReply(result.audioBase64);
    } else if (modeRef.current) {
      startListening();
    } else {
      setVoiceState('idle');
    }
  };

  const playReply = async (b64) => {
    stopPlayback();
    const done = () => {
      sourceRef.current = null;
      if (modeRef.current) startListening(); else setVoiceState('idle');
    };
    try {
      const ctx = ensureAudioCtx();
      // base64 → ArrayBuffer
      const bin = atob(b64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const buffer = await ctx.decodeAudioData(bytes.buffer);
      setVoiceState('speaking');
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      // Short gain fade in/out to avoid the click/pop ("interference") that a
      // raw buffer start/stop produces on the abrupt waveform edges.
      const gain = ctx.createGain();
      source.connect(gain);
      gain.connect(ctx.destination);
      const FADE = 0.03; // 30ms
      const t0 = ctx.currentTime;
      const dur = buffer.duration;
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.linearRampToValueAtTime(1, t0 + FADE);
      if (dur > FADE * 2) {
        gain.gain.setValueAtTime(1, t0 + dur - FADE);
        gain.gain.linearRampToValueAtTime(0.0001, t0 + dur);
      }
      source.onended = done;
      sourceRef.current = source;
      source.start(0);
    } catch (err) {
      console.error('Voice playback failed:', err);
      if (onErrorRef.current) onErrorRef.current('playback');
      done();
    }
  };

  // ---- Hands-free listening loop ----
  const startListening = async () => {
    try {
      const stream = await ensureStream();
      ensureAnalyser(stream);
      hadSpeechRef.current = false;
      lastVoiceRef.current = 0;
      utterStartRef.current = Date.now();
      startRecorder(stream);
      setVoiceState('listening');
      cleanupVad();
      vadTimerRef.current = setInterval(() => {
        if (!modeRef.current) return;
        const now = Date.now();
        if (currentRms() > RMS_THRESHOLD) {
          hadSpeechRef.current = true;
          lastVoiceRef.current = now;
        }
        const rec = recorderRef.current;
        if (!rec || rec.state !== 'recording') return;
        if (hadSpeechRef.current && now - lastVoiceRef.current > SILENCE_MS) {
          cleanupVad();
          rec.stop(); // → onstop → handleUtterance
        } else if (now - utterStartRef.current > MAX_UTTERANCE_MS) {
          cleanupVad();
          if (hadSpeechRef.current) {
            rec.stop();
          } else {
            rec._discard = true;
            rec.stop();
            startListening(); // restart fresh, nothing said yet
          }
        }
      }, 120);
    } catch (err) {
      if (onErrorRef.current) onErrorRef.current('mic');
      modeRef.current = false;
      setConversationMode(false);
      setVoiceState('idle');
    }
  };

  const toggleConversationMode = async () => {
    if (modeRef.current) {
      modeRef.current = false;
      setConversationMode(false);
      stopPlayback();
      releaseStream();
      setVoiceState('idle');
    } else {
      ensureAudioCtx(); // sync, inside the tap — unlocks audio playback
      modeRef.current = true;
      setConversationMode(true);
      await startListening();
    }
  };

  // ---- Push-to-talk ----
  const startPushToTalk = async () => {
    ensureAudioCtx(); // sync, inside the tap — unlocks audio playback
    try {
      const stream = await ensureStream();
      startRecorder(stream);
      setVoiceState('recording');
    } catch (err) {
      if (onErrorRef.current) onErrorRef.current('mic');
      setVoiceState('idle');
    }
  };

  const stopPushToTalk = () => {
    const rec = recorderRef.current;
    if (rec && rec.state === 'recording') rec.stop(); // → onstop → handleUtterance
  };

  const stopSpeaking = () => {
    stopPlayback();
    if (modeRef.current) startListening(); else setVoiceState('idle');
  };

  // Cleanup on unmount
  useEffect(() => () => {
    modeRef.current = false;
    stopPlayback();
    releaseStream();
    if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch (e) {} audioCtxRef.current = null; }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { voiceState, conversationMode, toggleConversationMode, startPushToTalk, stopPushToTalk, stopSpeaking };
}
